<script setup>
import { BarChart } from '@dashhudson/dashing-ui/bar-chart';
import { ChartCard } from '@dashhudson/dashing-ui/chart-card';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { MetricList } from '@dashhudson/dashing-ui/metric-list';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import { isEmpty } from 'lodash-es';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import {
  NO_DATA_FOUND,
  NO_DATA_FOUND_FOR_FILTERS,
  SOCIAL_LISTENING_DRAWERS,
  SOCIAL_LISTENING_REPORT_INTERACTION_TYPES,
  SOCIAL_LISTENING_REPORT_SECTIONS,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { socialListeningReportInteractionEvent } from '@/app/socialListening/utils/mixpanel-events';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { capitalize, scaleLabelFormatter } from '@/utils/formatters';

const route = useRoute();
const { launchDrawer } = useDrawer();

const comparisonStore = useSocialListeningComparisonsStore();

const isNoData = computed(() => {
  if (isEmpty(comparisonStore.postsPerTopicPerChannel)) {
    return true;
  }

  return Object.values(comparisonStore.postsPerTopicPerChannel).every((topic) =>
    Object.values(topic).every((count) => count === 0),
  );
});

const noDataMessage = computed(() =>
  isEmpty(comparisonStore.comparisonFilters) ? NO_DATA_FOUND : NO_DATA_FOUND_FOR_FILTERS,
);

const chartData = computed(() => {
  // Flatten and group data by topic
  const groupedByTopic = Object.fromEntries(
    Object.entries(comparisonStore.postsPerTopicPerChannel).map(([topic, channels]) => [
      topic,
      Object.entries(channels).map(([channel, mentions]) => ({
        x: capitalize(channel),
        y: mentions,
      })),
    ]),
  );

  // Convert grouped object into chart datasets
  const datasets = Object.entries(groupedByTopic)
    .map(([topic, dataPoints]) => ({
      label: topic,
      data: dataPoints,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { datasets };
});

const chartOptions = computed(() => ({
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      ticks: { callback: (value) => scaleLabelFormatter(value) },
    },
  },
  onClick: async (event, elements) => {
    const index = elements[0].index;
    const datasetIndex = elements[0].datasetIndex;

    const selectedTopic = comparisonStore.selectedComparison.topics[datasetIndex];
    const selectedChannelLabel = chartData.value.datasets[datasetIndex].data[index].x;
    const selectedChannel =
      selectedChannelLabel.toUpperCase() === 'X' ? 'TWITTER' : selectedChannelLabel.toUpperCase();

    const { sources, keywordsAndHashtags, ...filterData } = comparisonStore.temporaryFilters;
    comparisonStore.setDrawerSelectedTopic(selectedTopic);
    comparisonStore.setTempFilters({
      keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
      sources: [selectedChannel],
      ...filterData,
    });

    socialListeningReportInteractionEvent({
      reportType: SOCIAL_LISTENING_REPORT_SECTIONS.MENTIONS_BREAKDOWN,
      interactionType: SOCIAL_LISTENING_REPORT_INTERACTION_TYPES.DATA_POINT_CLICKED,
      path: route.path,
      reportDetails: {
        ...comparisonStore.drawerSerializedFilters,
        keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
        topicId: selectedTopic.id,
      },
    });

    await launchDrawer({
      name: SOCIAL_LISTENING_DRAWERS.TOP_POST,
      props: {
        ...comparisonStore.drawerSerializedFilters,
        keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
        topicId: selectedTopic.id,
      },
    });
  },
}));

function generateTooltip({ tooltip }) {
  const channelLabel = tooltip.title[0];

  const topicColorMap = {};
  if (tooltip.chart?.data?.datasets) {
    tooltip.chart.data.datasets.forEach((dataset) => {
      topicColorMap[dataset.label] = dataset.backgroundColor;
    });
  }

  const topicMentions = Object.entries(comparisonStore.postsPerTopicPerChannel)
    .map(([topic, channels]) => ({
      label: topic,
      value: scaleLabelFormatter(channels[channelLabel.toUpperCase()]),
      dot: {
        style: {
          backgroundColor: topicColorMap[topic],
        },
      },
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    title: capitalize(channelLabel),
    data: topicMentions,
  };
}
const activeTooltip = computed(() => (tooltipContext) => generateTooltip(tooltipContext));
</script>

<template>
  <Section class="gap-4">
    <SectionHeader title="Mentions Breakdown" />

    <ChartCard
      title="Mentions by Channel"
      :title-tooltip="TOPIC_COMPARISONS_TOOLTIPS.MENTIONS_BY_CHANNEL"
    >
      <BarChart
        :data="chartData"
        :loading="comparisonStore.pendingDataPull.topicsPostVolumePerChannel"
        :options="chartOptions"
        :no-data="isNoData"
        :no-data-message="noDataMessage"
        :show-legend="!isNoData"
      >
        <template #tooltip="tooltipContext">
          {{ activeTooltip(tooltipContext).title }}
          <MetricList :metric-items="activeTooltip(tooltipContext).data" />
          <span class="dash-text-subtle dash-text-base">Click a bar to view posts</span>
        </template>
      </BarChart>
    </ChartCard>
  </Section>
</template>
